import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import TimelineMax from 'TimelineMax';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';
import { observer, inject } from 'mobx-react';

const SIZE = 12;

const SIZE_DEFAULT = 'default';
const SIZE_MEDIUM = 'medium';
const SIZE_BIG = 'big';
const SIZE_NONE = 'none';

const SCALE_MAPPING = {
  [SIZE_DEFAULT]: 1,
  [SIZE_MEDIUM]: 1.7,
  [SIZE_BIG]: 3.33,
  [SIZE_NONE]: 0,
};

const GLOBAL_STATE_TEXT_MAPPING = {
  prev: 'prev',
  next: 'next',
  play: 'play',
};

const Styled = styled.div`
  position: fixed;
  top: -${SIZE / 2}px;
  left: -${SIZE / 2}px;
  width: ${SIZE}px;
  height: ${SIZE}px;
  z-index: 99;
  pointer-events: none;
  text-align: center;

  ${props =>
    props.hidden &&
    css`
      display: none;
    `};
`;

const Circle = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: #808080;
`;

const Text = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  font-size: 12px;
  font-weight: 300;
  z-index: 1;
`;

class Cursor extends React.PureComponent {
  static propTypes = {
    envDesktop: PropTypes.bool,
  };

  state = {
    size: SIZE_DEFAULT,
    text: '',
    globalState: '',
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.globalState !== prevState.globalState) {
      let size;
      let text;

      switch (nextProps.globalState) {
        case 'next':
        case 'prev':
        case 'play':
          size = SIZE_BIG;
          text = GLOBAL_STATE_TEXT_MAPPING[nextProps.globalState];
          break;
        case 'hidden':
          size = SIZE_NONE;
          text = '';
          break;
        default:
          size = SIZE_DEFAULT;
          text = '';
      }

      return {
        size,
        text,
        globalState: nextProps.globalState,
      };
    }

    return null;
  }

  componentDidMount() {
    if (this.props.envDesktop) {
      this.init();
    }
  }

  componentWillUnmount() {
    if (this.initialized) {
      document.removeEventListener('mousedown', this.mouseDownHandler);
      document.removeEventListener('mousemove', this.mouseMoveHandler);
      document.removeEventListener('resize', this.resizeHandler);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.size !== this.state.size) {
      TweenMax.to(this.circleNode, 0.35, {
        scale: SCALE_MAPPING[this.state.size],
        force3D: false,
        overwrite: 'none',
      });
    }
  }

  init() {
    this.initialized = true;
    this.updateValues();
    document.addEventListener('mousedown', this.mouseDownHandler);
    document.addEventListener('mousemove', this.mouseMoveHandler);
    document.addEventListener('resize', this.resizeHandler);
  }

  resizeHandler = debounce(() => {
    this.updateValues();
  }, 200);

  mouseMoveHandler = throttle(e => {
    TweenMax.set(this.cursorNode, { x: e.clientX, y: e.clientY });
    this.handleHover();
    this.handleScreenEdges(e.clientX, e.clientY);
  }, 20);

  handleScreenEdges(x, y) {
    const offset = 20;
    if (x <= offset || y <= offset || x >= this.viewportWidth - offset || y >= this.viewportHeight - offset) {
      this.setState({ size: SIZE_NONE });
    }
  }

  updateValues() {
    this.viewportWidth = window.outerWidth;
    this.viewportHeight = window.outerHeight;
  }

  handleHover() {
    if (this.state.globalState) {
      return;
    }

    const hoverElements = Array.from(document.querySelectorAll(':hover'));

    for (let i = 0; i < hoverElements.length; i++) {
      const el = hoverElements[i];

      if (el.getAttribute('data-hover-play')) {
        this.setState({ size: SIZE_BIG, text: 'play' });
        return;
      }

      if (el.getAttribute('data-hover-pause')) {
        this.setState({ size: SIZE_BIG, text: 'pause' });
        return;
      }

      if (el.nodeName === 'A' || el.nodeName === 'BUTTON' || el.getAttribute('data-link')) {
        this.setState({ size: SIZE_MEDIUM, text: '' });
        return;
      }
    }

    this.setState({ size: SIZE_DEFAULT, text: '' });
  }

  mouseDownHandler = () => {
    const scaleTo =
      this.state.size === SIZE_BIG ? SCALE_MAPPING[this.state.size] * 1.2 : SCALE_MAPPING[this.state.size] * 0.67;

    const tl = new TimelineMax();

    tl.to(this.circleNode, 0.15, { scale: scaleTo });
    tl.to(this.circleNode, 0.1, {
      scale: SCALE_MAPPING[this.state.size],
      onComplete: this.handleHover(),
    });
  };

  render() {
    const { envDesktop } = this.props;

    return (
      <Styled innerRef={node => (this.cursorNode = node)} hidden={!envDesktop}>
        <Circle innerRef={node => (this.circleNode = node)} />
        <Text>{this.state.text}</Text>
      </Styled>
    );
  }
}

export default inject(stores => ({
  cursorStore: stores.store.cursorStore,
  envStore: stores.store.envStore,
}))(
  observer(({ cursorStore, envStore, ...rest }) => (
    <Cursor {...rest} envDesktop={envStore.isUserAgentDesktop()} globalState={cursorStore.globalState} />
  ))
);



// WEBPACK FOOTER //
// ./src/components/Cursor.js